import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  ServiceReviewsState,
  CreateReviewRequest,
} from "./models/reviewModels";
import serviceReviewsApi from "./api/serviceReviewsApi";

export const createReview = createAsyncThunk(
  "serviceReviewsSlice/createReview",
  async (data: CreateReviewRequest) => {
    return serviceReviewsApi.createReview(data);
  }
);

export const checkHasReviewed = createAsyncThunk(
  "serviceReviewsSlice/checkHasReviewed",
  async (serviceRequestId: string) => {
    return serviceReviewsApi.checkHasReviewed(serviceRequestId);
  }
);

export const fetchReviewsForServiceRequest = createAsyncThunk(
  "serviceReviewsSlice/fetchReviewsForServiceRequest",
  async (serviceRequestId: string) => {
    return serviceReviewsApi.getReviewsForServiceRequest(serviceRequestId);
  }
);

export const fetchReviewSummary = createAsyncThunk(
  "serviceReviewsSlice/fetchReviewSummary",
  async (customerId: string) => {
    return serviceReviewsApi.getReviewSummary(customerId);
  }
);

const initialState: ServiceReviewsState = {
  reviews: [],
  reviewCheck: null,
  summary: null,
  loading: false,
  submitting: false,
  error: undefined,
};

const serviceReviewsSlice = createSlice({
  name: "serviceReviewsSlice",
  initialState,
  reducers: {
    clearReviews: (state) => {
      state.reviews = [];
      state.reviewCheck = null;
    },
    clearReviewError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      // createReview
      .addCase(createReview.pending, (state) => {
        state.submitting = true;
        state.error = undefined;
      })
      .addCase(createReview.fulfilled, (state, { payload }) => {
        state.submitting = false;
        state.reviews = [payload, ...state.reviews];
        if (state.reviewCheck) {
          state.reviewCheck = { hasReviewed: true, review: payload };
        }
      })
      .addCase(createReview.rejected, (state, { error }) => {
        state.submitting = false;
        state.error = error;
      })
      // checkHasReviewed
      .addCase(checkHasReviewed.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkHasReviewed.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.reviewCheck = payload;
      })
      .addCase(checkHasReviewed.rejected, (state) => {
        state.loading = false;
        state.reviewCheck = null;
      })
      // fetchReviewsForServiceRequest
      .addCase(fetchReviewsForServiceRequest.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReviewsForServiceRequest.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.reviews = payload;
      })
      .addCase(fetchReviewsForServiceRequest.rejected, (state, { error }) => {
        state.loading = false;
        state.error = error;
      })
      // fetchReviewSummary
      .addCase(fetchReviewSummary.fulfilled, (state, { payload }) => {
        state.summary = payload;
      })
      .addCase(fetchReviewSummary.rejected, (state) => {
        state.summary = null;
      });
  },
});

export const { clearReviews, clearReviewError } = serviceReviewsSlice.actions;

export default serviceReviewsSlice.reducer;
